import { useEffect, useState } from 'react';
import { Rnd } from 'react-rnd';
import { toast } from 'sonner';
import {
  Plus, FloppyDisk, Trash, TextT, Barcode, QrCode, Minus, Square,
  Tag, CopySimple, ArrowLeft,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DK_LABELS, LABEL_KINDS, LABEL_SAMPLE, sizeById } from '@/components/labels/labelSizes';
import { renderElementContent } from '@/components/labels/LabelCanvas';
import { labelApi } from '@/components/labels/labelApi';

const uid = () => Math.random().toString(36).slice(2, 9);
const SCALE = 4.2; // px per mm on the editor canvas
const CONT_DESIGN_H = 45; // mm design height for continuous rolls

const ELEMENT_TYPES = [
  { type: 'field', label: 'フィールド', Icon: Tag },
  { type: 'text', label: 'テキスト', Icon: TextT },
  { type: 'barcode', label: 'バーコード', Icon: Barcode },
  { type: 'qr', label: 'QRコード', Icon: QrCode },
  { type: 'line', label: '線', Icon: Minus },
  { type: 'box', label: '枠', Icon: Square },
];

function newElement(type, kind) {
  const base = { id: uid(), type, x: 2, y: 2 };
  if (type === 'text') return { ...base, w: 30, h: 7, text: 'テキスト', fontSize: 3.2, bold: false, align: 'left', wrap: false };
  if (type === 'field') {
    const f = LABEL_KINDS[kind].fields[0];
    return { ...base, w: 40, h: 7, field: f.key, fontSize: 3.2, bold: false, align: 'left', wrap: false, showLabel: false, labelText: f.label };
  }
  if (type === 'barcode') return { ...base, w: 46, h: 12 };
  if (type === 'qr') return { ...base, w: 16, h: 16 };
  if (type === 'line') return { ...base, w: 40, h: 0.5 };
  if (type === 'box') return { ...base, w: 34, h: 14, lineWidth: 0.4 };
  return { ...base, w: 24, h: 8 };
}

const blankTemplate = (kind = 'item', size_id = '29x90') => ({ name: '新しいテンプレート', kind, size_id, elements: [] });

function elVisualStyle(el) {
  if (el.type === 'line') return { width: '100%', height: '100%', background: '#000' };
  if (el.type === 'box') return { width: '100%', height: '100%', border: `${(el.lineWidth || 0.3) * SCALE}px solid #000`, boxSizing: 'border-box' };
  if (el.type === 'barcode' || el.type === 'qr') return { width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' };
  return {
    width: '100%', height: '100%', display: 'flex', alignItems: el.wrap ? 'flex-start' : 'center', overflow: 'hidden',
    justifyContent: el.align === 'center' ? 'center' : el.align === 'right' ? 'flex-end' : 'flex-start',
    textAlign: el.align || 'left',
    fontSize: `${(el.fontSize || 3) * SCALE}px`, fontWeight: el.bold ? 700 : 400,
    whiteSpace: el.wrap ? 'pre-wrap' : 'nowrap', wordBreak: el.wrap ? 'break-word' : 'normal',
    lineHeight: 1.1, color: '#000',
  };
}

export default function LabelDesigner() {
  const [templates, setTemplates] = useState([]);
  const [current, setCurrent] = useState(null); // template being edited
  const [selId, setSelId] = useState(null);      // selected element id
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () => labelApi.list().then(setTemplates).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const size = current ? sizeById(current.size_id) : null;
  const canvasW = size ? size.w : 0;
  const canvasH = size ? (size.h || CONT_DESIGN_H) : 0;
  const sample = current ? LABEL_SAMPLE[current.kind] : null;
  const sel = current?.elements.find((e) => e.id === selId) || null;

  const patch = (fields) => setCurrent((c) => ({ ...c, ...fields }));
  const updateEl = (id, fields) => setCurrent((c) => ({ ...c, elements: c.elements.map((e) => (e.id === id ? { ...e, ...fields } : e)) }));
  const addEl = (type) => { const el = newElement(type, current.kind); setCurrent((c) => ({ ...c, elements: [...c.elements, el] })); setSelId(el.id); };
  const delEl = (id) => { setCurrent((c) => ({ ...c, elements: c.elements.filter((e) => e.id !== id) })); if (selId === id) setSelId(null); };
  const dupEl = (id) => {
    const src = current.elements.find((e) => e.id === id); if (!src) return;
    const copy = { ...src, id: uid(), x: (src.x || 0) + 3, y: (src.y || 0) + 3 };
    setCurrent((c) => ({ ...c, elements: [...c.elements, copy] })); setSelId(copy.id);
  };

  const save = async () => {
    if (!current?.name?.trim()) { toast.error('テンプレート名を入力してください'); return; }
    setSaving(true);
    try {
      const body = { name: current.name.trim(), kind: current.kind, size_id: current.size_id, elements: current.elements };
      const saved = current.id ? await labelApi.update(current.id, body) : await labelApi.create(body);
      setCurrent(saved); await load(); toast.success('保存しました');
    } catch (e) { toast.error(e.response?.data?.detail || '保存に失敗しました'); }
    finally { setSaving(false); }
  };

  const removeTemplate = async () => {
    if (!current?.id) { setCurrent(null); return; }
    if (!window.confirm(`「${current.name}」を削除しますか？`)) return;
    try { await labelApi.remove(current.id); toast.success('削除しました'); setCurrent(null); load(); }
    catch { toast.error('削除に失敗しました'); }
  };

  /* ── list view ─────────────────────────────────────────────────────── */
  if (!current) {
    return (
      <div className="max-w-5xl mx-auto p-4 sm:p-6" data-testid="label-designer">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-xl font-semibold text-zinc-900 flex items-center gap-2"><Tag size={22} weight="duotone" className="text-blue-600" /> ラベルデザイナー</h1>
            <p className="text-sm text-zinc-500 mt-0.5">Brother QL-800 用ラベルのレイアウトを自由に作成・保存できます。印刷時に選択して使用します。</p>
          </div>
          <Button onClick={() => { setCurrent(blankTemplate()); setSelId(null); }} data-testid="label-new-btn" className="bg-teal-700 hover:bg-teal-800 text-white">
            <Plus size={16} weight="bold" className="mr-1" /> 新規作成
          </Button>
        </div>
        {loading ? (
          <p className="text-sm text-zinc-400 py-10 text-center">読み込み中…</p>
        ) : templates.length === 0 ? (
          <div className="border-2 border-dashed border-zinc-200 rounded-xl py-16 text-center text-zinc-400">
            <Tag size={40} className="mx-auto mb-2 opacity-40" />
            <p className="text-sm">まだテンプレートがありません。「新規作成」から作成してください。</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {templates.map((t) => (
              <button key={t.id} onClick={() => { setCurrent(JSON.parse(JSON.stringify(t))); setSelId(null); }}
                data-testid={`label-template-${t.id}`}
                className="text-left bg-white border border-zinc-200 rounded-xl p-4 hover:border-blue-400 hover:shadow-md transition-all">
                <p className="font-semibold text-zinc-900 truncate">{t.name}</p>
                <div className="mt-1.5 flex items-center gap-2 text-[11px]">
                  <span className="bg-blue-50 text-blue-700 rounded px-1.5 py-0.5 font-medium">{LABEL_KINDS[t.kind]?.label || t.kind}</span>
                  <span className="bg-zinc-100 text-zinc-600 rounded px-1.5 py-0.5">{sizeById(t.size_id).roll} · {sizeById(t.size_id).w}{sizeById(t.size_id).h ? `×${sizeById(t.size_id).h}` : ''}mm</span>
                  <span className="text-zinc-400 ml-auto">{t.elements?.length || 0} 要素</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  /* ── editor view ───────────────────────────────────────────────────── */
  return (
    <div className="max-w-6xl mx-auto p-3 sm:p-5" data-testid="label-designer-editor">
      <style>{`
        .ldz-canvas svg{ height:100%; width:auto; max-width:100%; display:block; }
        .ldz-canvas .ql-qr svg{ height:100%; width:auto; }
      `}</style>

      {/* header */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Button variant="ghost" size="sm" onClick={() => { setCurrent(null); setSelId(null); }} data-testid="label-back-btn"><ArrowLeft size={16} className="mr-1" /> 一覧</Button>
        <Input value={current.name} onChange={(e) => patch({ name: e.target.value })} data-testid="label-name-input"
          className="max-w-[220px] font-semibold" placeholder="テンプレート名" />
        <select value={current.kind} onChange={(e) => { patch({ kind: e.target.value }); }} data-testid="label-kind-select"
          className="text-sm border border-zinc-300 rounded-lg px-2.5 py-2 bg-white">
          {Object.entries(LABEL_KINDS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={current.size_id} onChange={(e) => patch({ size_id: e.target.value })} data-testid="label-size-select"
          className="text-sm border border-zinc-300 rounded-lg px-2.5 py-2 bg-white">
          {DK_LABELS.map((s) => <option key={s.id} value={s.id}>{s.roll} · {s.w}{s.h ? `×${s.h}` : '×連続'}mm</option>)}
        </select>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={removeTemplate} data-testid="label-delete-btn" className="text-rose-600 border-rose-200 hover:bg-rose-50"><Trash size={15} className="mr-1" /> 削除</Button>
          <Button size="sm" onClick={save} disabled={saving} data-testid="label-save-btn" className="bg-teal-700 hover:bg-teal-800 text-white"><FloppyDisk size={15} className="mr-1" /> 保存</Button>
        </div>
      </div>

      {/* toolbar */}
      <div className="flex flex-wrap items-center gap-1.5 mb-3">
        <span className="text-xs text-zinc-500 mr-1">要素を追加:</span>
        {ELEMENT_TYPES.map(({ type, label, Icon }) => (
          <button key={type} onClick={() => addEl(type)} data-testid={`label-add-${type}`}
            className="inline-flex items-center gap-1 text-xs bg-white border border-zinc-200 rounded-lg px-2.5 py-1.5 hover:border-blue-400 hover:text-blue-600 transition-colors">
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-[1fr_260px] gap-4">
        {/* canvas */}
        <div className="bg-zinc-100 rounded-xl p-6 flex items-center justify-center overflow-auto min-h-[280px]" onClick={() => setSelId(null)}>
          <div className="ldz-canvas relative bg-white shadow-lg" data-testid="label-canvas"
            style={{ width: canvasW * SCALE, height: canvasH * SCALE, outline: '1px solid #d4d4d8' }}
            onClick={(e) => e.stopPropagation()}>
            {current.elements.map((el) => (
              <Rnd key={el.id} bounds="parent"
                size={{ width: (el.w || 10) * SCALE, height: (el.h || 6) * SCALE }}
                position={{ x: (el.x || 0) * SCALE, y: (el.y || 0) * SCALE }}
                onDragStart={() => setSelId(el.id)}
                onDragStop={(e, d) => {
                  // Ignore micro-movements (< 2px): a plain click must not move the element.
                  const moved = Math.abs(d.x - (el.x || 0) * SCALE) >= 2 || Math.abs(d.y - (el.y || 0) * SCALE) >= 2;
                  if (!moved) return;
                  updateEl(el.id, { x: +(d.x / SCALE).toFixed(1), y: +(d.y / SCALE).toFixed(1) });
                }}
                onResizeStop={(e, dir, ref, delta, pos) => updateEl(el.id, {
                  w: +(ref.offsetWidth / SCALE).toFixed(1), h: +(ref.offsetHeight / SCALE).toFixed(1),
                  x: +(pos.x / SCALE).toFixed(1), y: +(pos.y / SCALE).toFixed(1),
                })}
                onMouseDown={() => setSelId(el.id)}
                style={{ outline: selId === el.id ? '2px solid #2563eb' : '1px dashed rgba(0,0,0,0.15)', cursor: 'move', zIndex: selId === el.id ? 10 : 1 }}>
                <div style={elVisualStyle(el)} data-testid={`label-el-${el.id}`}>{renderElementContent(el, sample)}</div>
              </Rnd>
            ))}
          </div>
        </div>

        {/* properties */}
        <div className="bg-white border border-zinc-200 rounded-xl p-3.5">
          {!sel ? (
            <p className="text-xs text-zinc-400 py-6 text-center">要素を選択すると<br />ここで編集できます。</p>
          ) : (
            <div className="space-y-3" data-testid="label-props">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-zinc-700">{ELEMENT_TYPES.find((x) => x.type === sel.type)?.label}</span>
                <div className="flex gap-1">
                  <button onClick={() => dupEl(sel.id)} data-testid="label-el-dup" className="p-1 text-zinc-400 hover:text-blue-600" title="複製"><CopySimple size={15} /></button>
                  <button onClick={() => delEl(sel.id)} data-testid="label-el-del" className="p-1 text-zinc-400 hover:text-rose-600" title="削除"><Trash size={15} /></button>
                </div>
              </div>

              {sel.type === 'text' && (
                <label className="block text-xs text-zinc-500">内容
                  <Input value={sel.text || ''} onChange={(e) => updateEl(sel.id, { text: e.target.value })} data-testid="label-el-text" className="mt-1 h-8" />
                </label>
              )}
              {sel.type === 'field' && (
                <>
                  <label className="block text-xs text-zinc-500">項目
                    <select value={sel.field} onChange={(e) => { const f = LABEL_KINDS[current.kind].fields.find((x) => x.key === e.target.value); updateEl(sel.id, { field: e.target.value, labelText: f?.label }); }}
                      data-testid="label-el-field" className="mt-1 w-full text-sm border border-zinc-300 rounded-lg px-2 py-1.5 bg-white">
                      {LABEL_KINDS[current.kind].fields.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                    </select>
                  </label>
                  <label className="flex items-center gap-2 text-xs text-zinc-600">
                    <input type="checkbox" checked={!!sel.showLabel} onChange={(e) => updateEl(sel.id, { showLabel: e.target.checked })} data-testid="label-el-showlabel" />
                    見出しを表示（例: {sel.labelText}: ）
                  </label>
                </>
              )}
              {(sel.type === 'text' || sel.type === 'field') && (
                <>
                  <label className="block text-xs text-zinc-500">文字サイズ (mm): {sel.fontSize}
                    <input type="range" min="1.5" max="12" step="0.1" value={sel.fontSize || 3} onChange={(e) => updateEl(sel.id, { fontSize: +e.target.value })} data-testid="label-el-fontsize" className="w-full" />
                  </label>
                  <div className="flex gap-2 flex-wrap">
                    <button onClick={() => updateEl(sel.id, { bold: !sel.bold })} data-testid="label-el-bold" className={`text-xs px-2.5 py-1.5 rounded-lg border ${sel.bold ? 'bg-blue-50 border-blue-300 text-blue-700 font-bold' : 'border-zinc-200 text-zinc-500'}`}>太字</button>
                    <button onClick={() => updateEl(sel.id, { wrap: !sel.wrap })} data-testid="label-el-wrap" className={`text-xs px-2.5 py-1.5 rounded-lg border ${sel.wrap ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-zinc-200 text-zinc-500'}`} title="長いテキストを枠内で折り返します">折り返し</button>
                    {['left', 'center', 'right'].map((a) => (
                      <button key={a} onClick={() => updateEl(sel.id, { align: a })} data-testid={`label-el-align-${a}`} className={`text-xs px-2.5 py-1.5 rounded-lg border ${sel.align === a ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-zinc-200 text-zinc-500'}`}>{a === 'left' ? '左' : a === 'center' ? '中' : '右'}</button>
                    ))}
                  </div>
                </>
              )}
              {(sel.type === 'barcode' || sel.type === 'qr') && (
                <p className="text-[11px] text-zinc-500 bg-zinc-50 rounded-lg p-2">この{sel.type === 'qr' ? 'QRコード' : 'バーコード'}は各ラベルの識別コード（{LABEL_KINDS[current.kind].label}のコード）を表示します。枠のサイズで大きさを調整できます。</p>
              )}
              {sel.type === 'box' && (
                <label className="block text-xs text-zinc-500">線の太さ (mm): {sel.lineWidth}
                  <input type="range" min="0.2" max="2" step="0.1" value={sel.lineWidth || 0.3} onChange={(e) => updateEl(sel.id, { lineWidth: +e.target.value })} className="w-full" />
                </label>
              )}
              <div className="grid grid-cols-2 gap-2 pt-1">
                {['x', 'y', 'w', 'h'].map((k) => (
                  <label key={k} className="text-[11px] text-zinc-500 uppercase">{k} (mm)
                    <Input type="number" step="0.5" value={sel[k] ?? 0} onChange={(e) => updateEl(sel.id, { [k]: +e.target.value })} className="mt-0.5 h-7 text-xs" />
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <p className="text-[11px] text-zinc-400 mt-3">💡 ドラッグで移動、四隅で拡大縮小。サンプルデータでプレビュー表示しています。印刷時は各ラベルの実データに置き換わります。</p>
    </div>
  );
}
